import { BookNow, BookNowStatuses, JobBids, JSONApiDataObject } from 'Douleutaras/types';
import React from 'react';
import { Trans } from 'react-i18next';
import {
  BID_MILESTONES,
  BOOK_NOW_STATUS_TO_MILESTONE_STEP,
  NEED_HELP_MILESTONES
} from 'Douleutaras/constants/ccf/need-help-options';

interface IMilestoneSteps {
  bookNowEntry?: JSONApiDataObject<BookNow>;
  bid?: JSONApiDataObject<JobBids>;
  bidBuyerEntryDate?: string;
  paymentAvailable?: boolean;
}

// Configuration that is responsible for displaying information based on a BookNow statuses
export const getMilestoneStep = ({ bookNowEntry, bid, bidBuyerEntryDate, paymentAvailable }: IMilestoneSteps) => bookNowEntryStatus => {
  const jobStartDate = bidBuyerEntryDate ?? bid?.attributes.start;
  const meetingDatetime = bookNowEntry?.attributes?.meetingDatetime;
  const stepNumber = BOOK_NOW_STATUS_TO_MILESTONE_STEP[bookNowEntryStatus];

  switch (bookNowEntryStatus) {
    default:
    case BookNowStatuses.JOB_POST:
      return {
        stepNumber,
        label: <Trans>Job Post</Trans>
      };
    case BookNowStatuses.ACTIVE:
      return {
        stepNumber,
        label: BID_MILESTONES[NEED_HELP_MILESTONES.INITIAL_CONTACT_MILESTONE].title,
        stepInfo: <Trans>T.CD.MILE.ACTIVE</Trans>
      };
    case BookNowStatuses.MEETING_NOT_SCHEDULED:
      return {
        stepNumber,
        failed: true,
        label: <Trans>Something went wrong</Trans>,
        stepInfo: paymentAvailable
          ? <Trans>T.CD.MILE.PAYMENT-AVAILABLE.CONTACT-PRO-AGAIN</Trans>
          : <Trans>T.CD.MILE.MEETING-NOT-SCHEDULED</Trans>
      };
    case BookNowStatuses.MEETING_SCHEDULED:
      return {
        date: meetingDatetime,
        stepNumber,
        label: BID_MILESTONES[NEED_HELP_MILESTONES.INTRODUCTORY_MEETING_MILESTONE].title,
        stepInfo: <Trans>T.CD.MILE.MEETING-SCHEDULED</Trans>
      };
    case BookNowStatuses.MEETING_DONE:
      return {
        date: meetingDatetime,
        stepNumber,
        label: BID_MILESTONES[NEED_HELP_MILESTONES.INTRODUCTORY_MEETING_MILESTONE].title,
        stepInfo: <Trans>T.CD.MILE.MEETING-DONE</Trans>
      };
    case BookNowStatuses.TO_FOLLOW_UP:
      return {
        date: meetingDatetime,
        stepNumber,
        label: BID_MILESTONES[NEED_HELP_MILESTONES.INTRODUCTORY_MEETING_MILESTONE].title,
        stepInfo: <Trans>T.CD.MILE.TO-FOLLOW-UP</Trans>
      };
    case BookNowStatuses.MEETING_NOT_DONE:
      return {
        stepNumber,
        date: meetingDatetime,
        failed: true,
        label: <Trans>Something went wrong</Trans>,
        stepInfo: paymentAvailable
          ? <Trans>T.CD.MILE.PAYMENT-AVAILABLE.CONTACT-PRO-AGAIN</Trans>
          : <Trans>T.CD.MILE.MEETING-NOT-DONE</Trans>
      };
    case BookNowStatuses.QUOTE_NOT_SENT:
      return {
        failed: true,
        stepNumber,
        label: <Trans>Something went wrong</Trans>,
        stepInfo: paymentAvailable
          ? <Trans>T.CD.MILE.PAYMENT-AVAILABLE.CONTACT-PRO-AGAIN</Trans>
          : <Trans>T.CD.MILE.QUOTE-NOT-SENT</Trans>
      };
    case BookNowStatuses.QUOTE_NOT_ACCEPTED:
      return {
        failed: true,
        stepNumber,
        label: <Trans>Something went wrong</Trans>,
        stepInfo: paymentAvailable
          ? <Trans>T.CD.MILE.PAYMENT-AVAILABLE.CONTACT-PRO-AGAIN</Trans>
          : <Trans>T.CD.MILE.QUOTE-NOT-ACCEPTED</Trans>,
        stepInfoCost: true
      };
    case BookNowStatuses.QUOTE_CUSTOMER_THINKING:
      return {
        stepNumber,
        label: BID_MILESTONES[NEED_HELP_MILESTONES.OFFER_MILESTONE].title,
        stepInfo: <Trans>T.CD.MILE.QUOTE-CUSTOMER-THINKING</Trans>,
        stepInfoCost: true
      };
    case BookNowStatuses.QUOTE_SENT:
      return {
        stepNumber,
        label: BID_MILESTONES[NEED_HELP_MILESTONES.OFFER_MILESTONE].title,
        stepInfo: paymentAvailable
          ? <Trans>T.CD.MILE.PAYMENT-AVAILABLE.QUOTE-SENT</Trans>
          : <Trans>T.CD.MILE.QUOTE-SENT</Trans>,
        stepInfoCost: true,
        moneyBackGuarantee: paymentAvailable
      };
    case BookNowStatuses.QUOTE_ACCEPTED:
      return {
        date: jobStartDate,
        stepNumber,
        label: BID_MILESTONES[NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE].title,
        stepInfo: paymentAvailable ? undefined : <Trans>T.CD.MILE.QUOTE-ACCEPTED</Trans>,
        stepInfoCost: true,
        moneyBackGuarantee: paymentAvailable
      };
    case BookNowStatuses.JOB_STARTED:
      return {
        date: jobStartDate,
        stepNumber,
        label: BID_MILESTONES[NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE].title,
        stepInfo: <Trans>T.CD.MILE.JOB-STARTED</Trans>,
        stepInfoCost: true
    };
    case BookNowStatuses.JOB_WILL_NOT_START:
      return {
        date: jobStartDate,
        failed: true,
        stepNumber,
        label: <Trans>Something went wrong</Trans>,
        stepInfo: paymentAvailable
          ? <Trans>T.CD.MILE.PAYMENT-AVAILABLE.CONTACT-PRO-AGAIN</Trans>
          : <Trans>T.CD.MILE.JOB-WILL-NOT-START</Trans>,
        stepInfoCost: true
      };
    case BookNowStatuses.CANNOT_BE_COMPLETED:
      return {
        date: jobStartDate,
        failed: true,
        stepNumber,
        label: <Trans>Something went wrong</Trans>,
        stepInfo: paymentAvailable
          ? <Trans>T.CD.MILE.PAYMENT-AVAILABLE.CONTACT-PRO-AGAIN</Trans>
          : <Trans>T.CD.MILE.CANNOT-BE-COMPLETED</Trans>,
        stepInfoCost: true
      };
    case BookNowStatuses.COMPLETION_PENDING:
      return {
        date: jobStartDate,
        stepNumber,
        label: BID_MILESTONES[NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE].title,
        stepInfo: <Trans>T.CD.MILE.COMPLETION-PENDING</Trans>,
        stepInfoCost: true
      };
    case BookNowStatuses.COMPLETED:
      return {
        stepNumber,
        isComplete: true,
        label: BID_MILESTONES[NEED_HELP_MILESTONES.COMPLETE_THE_JOB_MILESTONE].title,
        stepInfo: paymentAvailable ? <Trans>T.CD.MILE.PAYMENT-AVAILABLE.COMPLETED</Trans> : <Trans>T.CD.MILE.COMPLETED</Trans>,
        stepInfoCost: true
      };
  }
};
